<template>
  <div class="language-selector">
    <v-menu location="bottom" :close-on-content-click="true">
      <template #activator="{ props }">
        <v-btn
          class="language-btn"
          v-bind="props"
          variant="text"
          density="comfortable"
        >
        <img :src="selectedLanguage.flag" class="flag-icon" :alt="selectedLanguage.name" />
          <span class="language-name" v-if="showLanguageName">{{ selectedLanguage.name }}</span>
          <span class="language-code" v-else>{{ selectedLanguage.code }}</span>
        </v-btn>
      </template>
      <v-card min-width="200">
        <v-list>
          <v-list-item
            v-for="language in languages"
            :key="language.code"
            :value="language.code"
            @click="selectLanguage(language)"
            class="language-item"
          >
            <template #prepend>
              <img :src="language.flag" class="flag-icon" :alt="language.name" />
            </template>
            <v-list-item-title>{{ language.name }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>
    
    <!-- Language Preference Modal -->
    <v-dialog v-model="showChatLangModal" max-width="500px">
      <v-card>
        <v-card-title class="headline">{{ translation('languageMenu.title') }}</v-card-title>
        <v-card-text>
          {{ translation('languageMenu.description') }} <br /> <br />
          
          {{ translation('languageMenu.descriptionTip') }}
          <img :src="LanguageIcon" />
          {{ translation('languageMenu.descriptionTip2') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="closeChatLangModal">{{ translation('languageMenu.actionNo') }}</v-btn>
          <v-btn color="primary" variant="elevated" @click="changeChatLanguage">{{ translation('languageMenu.actionYes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useStore } from 'vuex';
import LanguageIcon from '@/assets/icons/language.svg';
import { useCustomT } from '@/composables/useCustomT';

const translation = useCustomT();
const store = useStore();

const userProfile = computed(() => store.state.userProfile);
const locale = computed(() => store.state.locale);

// Add ref for modal visibility
const showChatLangModal = ref(false);
// Store the selected language code for later use in the modal response
const pendingLanguageCode = ref(null);

const props = defineProps({
  showLanguageName: {
    type: Boolean,
    default: false
  },
  storageKey: {
    type: String,
    default: 'selectedLocale'
  }
});

const languages = [
  { 
    code: 'en', 
    name: 'English',
    flag: '/flags/gb.svg'
  },
  { 
    code: 'es', 
    name: 'Español',
    flag: '/flags/es.svg'
  },
  /*  
  { 
    code: 'fr', 
    name: 'Français',
    flag: '/flags/fr.svg'
  },
  */
  { 
    code: 'de', 
    name: 'Deutsch',
    flag: '/flags/de.svg'
  },
  { 
    code: 'nl', 
    name: 'Nederlands',
    flag: '/flags/nl.svg'
  },
  /*
  { 
    code: 'pt', 
    name: 'Português',
    flag: '/flags/pt.svg'
  },
  */
];

const selectedLanguage = ref(languages[0]);

// Load language from localStorage on component mount
onMounted(() => {
  const savedLanguage = localStorage.getItem(props.storageKey);
 
  if (savedLanguage) {
    const foundLanguage = languages.find(lang => lang.code === savedLanguage);
    if (foundLanguage) {
      selectedLanguage.value = foundLanguage;
      locale.value = foundLanguage.code;
    }
  }
});

// Function to open the modal
const openChatLangModal = (languageCode) => {
  pendingLanguageCode.value = languageCode;
  showChatLangModal.value = true;
};

// Function to close the modal
const closeChatLangModal = () => {
  showChatLangModal.value = false;
  pendingLanguageCode.value = null;
};


const changeChatLanguage = () => {
  const languageCode = userProfile.value.country;
  const copyLang = userProfile.value.userLanguage;
  userProfile.value.userLanguage = languageCode;
  if (languageCode !== copyLang) {
    store.dispatch('updateUserProfile', userProfile.value);

    store.dispatch('trackEvent', {
      event: 'ChatResponseLanguage-updated-via-menu',
      values: {
        content: {
          uid: userProfile.value.uid,
          userLanguage: userProfile.value.userLanguage,
        },
      },
    });
    store.commit('SET_STATE_PROPERTY', { property: 'stayInChat', value: true });
  }

  closeChatLangModal();
};

// Handle language selection
const selectLanguage = async (language) => {

  if (language.code === selectedLanguage.value.code) {
    return;
  }
  
  userProfile.value.country = language.code;
 
  try {
    await store.dispatch('updateUserProfile', userProfile.value);
    store.commit('SET_STATE_PROPERTY', { property: 'locale', value: language.code });
    localStorage.setItem('selectedLocale', language.code);

    store.dispatch('trackEvent', {
      event: 'UILanguageChanged-via-user-menu',
      values: {
        content: {
          uid: userProfile.value.uid,
          country: language.code || CHAT_RESPONSE_LANGUAGE_BY_DEFAULT,
        },
      },
    });
    store.commit('SET_STATE_PROPERTY', { property: 'stayInChat', value: true });
    
    if (userProfile.value.country !== userProfile.value.userLanguage) {
      openChatLangModal(language.code);
    }
  } catch (error) {
    console.error('error', error);
  }
 
};

// Watch for external locale changes and sync with our component
watch(locale, (newLocale) => {
  const matchingLanguage = languages.find(lang => lang.code === newLocale);
  if (matchingLanguage && matchingLanguage.code !== selectedLanguage.value.code) {
    selectedLanguage.value = matchingLanguage;
    localStorage.setItem(props.storageKey, matchingLanguage.code);
  }
});
</script>

<style scoped>
.language-selector {
  display: inline-block;
}

.language-btn {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.language-btn:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.language-flag {
  margin-right: 8px;
}

.language-name {
  font-size: 14px;
  text-transform: none;
}

.language-code {
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 500;
}

.language-item {
  cursor: pointer;
}

.flag-icon {
  width: 24px;
  height: 16px;
  margin-right: 8px;
  object-fit: contain;
}

/* Mobile styles */
.mobile-sidebar__content .language-selector {
  display: block;
  width: 100%;
}

.mobile-sidebar__content .v-menu {
  width: 100%;
}

.mobile-sidebar__content .language-btn {
  width: 100%;
  justify-content: flex-start;
}

.mobile-sidebar__content .v-card {
  width: 100%;
}
</style>
